/* Generated by restful-react */

import React from 'react';
import { Get, GetProps, useGet, UseGetProps, Mutate, MutateProps, useMutate, UseMutateProps } from 'restful-react';

import { useAtmApiBaseUrl, useAtmApiRequestOptions } from './ApiProvider';

export const SPEC_VERSION = '2025-08-15';

/**
 * A description of the form fields. Use this to build the form or, if you already submitted data, to rebuild it.
 */
export type FormFieldsArray = {
  name?: string;
  label?: string;
  /**
   * Pass this as type to the input.
   *
   * The special type `compound` means that this property consist of multiple form fields,
   * render them all in a row with one common label. Compound fields have no value proeprty
   * (The values are on the children fields). See `children` below.
   *
   * The special type `choice` represents a select. See `choices` below.
   *
   */
  type?: string;
  /**
   * If this field is required for submitting the form.
   */
  required?: boolean;
  /**
   * If the field is of type `compund`, this will contain the children form fields.
   */
  children?: {
    /**
     * This and the main field name result in the actual name you have to pass to your input, e.g. "color[0]".
     */
    name?: string;
    type?: string;
    required?: boolean;
    choices?: {
      label?: string;
      value?: string;
    }[];
    /**
     * The previous value of this field. Only present if there was a previous value.
     */
    value?: string;
  }[];
  /**
   * If the field is of type `choice`, this will contain the possible choices.
   */
  choices?: {
    label?: string;
    value?: string;
  }[];
  /**
   * Translated description of the error. Only present if there was an error with this field.
   */
  error?: string;
  /**
   * The base message used to translate the error. Use this if you want to handle some error directly. Like suggesting the user to log in if the email is already taken when registering.
   */
  errorKey?: string;
  /**
   * The previous value of this field. Only present if there was a previous value.
   */
  value?: string;
}[];

/**
 * What data should the statistic contain?
 *
 * New statistic data always completely replaces the old one, you always have to send all statistics
 * that should be shown in the admin backend to the SBK.
 *
 * Statistics are divided into rows and columns (2 dimensional array). Every row can have two columns.
 * If one statistic should span two columns, you can set `"columns": 2,` on the statistic.
 *
 */
export type StatisticsArray = {
  /**
   * The name of the statistic. Can be anything. Use it to describe the current
   * statistic.
   *
   */
  name?: string;
  /**
   * Every row can have two columns. If one statistic should span two columns, you can
   * set this to 2.
   *
   */
  columns?: number;
  /**
   * The type of the statistic. This defines the format of the statistic data below.
   *
   */
  type?: 'survey-results' | 'table';
  /**
   * The statistic data. This is different for every statistic type. See below for
   * examples.
   *
   * ### Type "survey-results"
   * ```json
   * {
   *   "Allgemein": [
   *     {
   *       "name": "Altersverteilung",
   *       "answers": [
   *         {
   *           "name": "0-25 Jahre",
   *           "value": 9
   *         },
   *         {
   *           "name": "26-35 Jahre",
   *           "value": 13
   *         },
   *         {
   *           "name": "36-45 jahre",
   *           "value": 5
   *         },
   *         {
   *           "name": "46-55 Jahre",
   *           "value": 10
   *         },
   *         {
   *           "name": "56 Jahre und älter",
   *           "value": 6
   *         }
   *       ]
   *     }
   *   ],
   *   "Allgemeiner Gesundsheitszustand": [
   *     {
   *       "name": "Wie würden Sie Ihren Gesundheitszustand im Allgeminen beschreiben?",
   *       "answers": [
   *         {
   *           "name": "Sehr gut",
   *           "value": 5
   *         },
   *         {
   *           "name": "Gut",
   *           "value": 29
   *         },
   *         {
   *           "name": "Eher gut",
   *           "value": 5
   *         },
   *         {
   *           "name": "Eher schlecht",
   *           "value": 3
   *         },
   *         {
   *           "name": "Schlecht",
   *           "value": 1
   *         },
   *         {
   *           "name": "Sehr schlecht",
   *           "value": 0
   *         }
   *       ]
   *     }
   *   ]
   * }
   * ```
   *
   * ### Type "table"
   * "data" is two dimensional array with the table rows and columns. Every column can
   * either be a scalar value (string, int, float, etc.) or a value object with custom
   * style defintions.
   * ```json
   * [
   *   [
   *     "Value of first column in first row",
   *     "Value of second column in first row",
   *     "Value of third column in first row"
   *   ],
   *   [
   *     "Value of first column in second row",
   *     "",
   *     "<- The previous column is empty"
   *   ],
   *   [
   *     "First column in third row",
   *     {
   *       "value": "Value with custom style, you can use whatever CSS you want",
   *       "style": "font-weight: bolder;background-color: red;"
   *     },
   *     {
   *       "value": "In addition to \"style\", \"rowspan\" and \"colspan\" are also supported",
   *       "style": "font-style:italic;background-color:green;",
   *       "rowspan": 2
   *     }
   *   ],
   *   [
   *     {
   *       "value": "Colspan-Test",
   *       "style": "background-color: black;color: white;border-top:0;",
   *       "colspan": 2
   *     }
   *   ],
   *   [
   *     "All scalar values are supported:",
   *     34,
   *     45.6
   *   ],
   *   [
   *     "",
   *     "",
   *     "Line-breaks are\nsupported\ntoo"
   *   ]
   * ]
   * ```
   *
   * ### Legacy format for type "table" when you have a defined header and body
   * ```json
   * {
   *   "headers": [
   *     "Platz",
   *     "Teilnehmer",
   *     "Getrunkene Liter"
   *   ],
   *   "values": [
   *     [1, "Moritz Test", 1002],
   *     [2, "Manuela Müller", 992],
   *     [3, "Franz Peter", 903],
   *     [4, "Max Müller", 763],
   *     [5, "Claudia Hensen", 700],
   *     [6, "Max Mustermann", 588],
   *     [7, "Peter Stübe", 486],
   *     [8, "Lena Kowitz", 333],
   *     [9, "Micha Lauter", 330],
   *     [10, "Romi Leiser", 299]
   *   ]
   * }
   * ```
   *
   */
  data?: {}[];
}[][];

export interface ActivateAttendeeRequest {
  key: {};
  password?: {};
}

export interface AppointmentReadAppointments {
  id?: number;
  start?: string;
  end?: string;
  location?: any;
  room?: any;
  serviceProvider?: any;
  informationString?: string;
  title?: string;
}

export interface AppointmentReadReadAppointments {
  id?: number;
  start?: string;
  end?: string;
  location?: any;
  room?: any;
  serviceProvider?: any;
  informationString?: string;
  title?: string;
}

export interface AppointmentReservationRead {
  reservationKey?: string;
  createdAt?: string;
}

export interface AppointmentReservationWrite {
  reservationKey?: string;
  appointment: string;
}

export interface AppointmentWaitingListEntryRead {
  id?: number;
}

export interface AppointmentWaitingListEntryWrite {
  campaign?: string;
  registrationData?: any[];
}

export interface AttendeeAttendeeReadId {
  id?: number;
}

export interface AttendeeRead {
  id?: number;
  salutation?: number;
  title?: number;
  firstName: string;
  lastName: string;
  language?: string;
  dateOfBirth?: any;
  insurance: any;
  policyNumber?: any;
  telBusiness?: any;
  telPrivate?: any;
  email: string;
}

export interface AttendeeWriteJsonMergePatch {
  salutation?: number;
  title?: number;
  firstName?: string;
  lastName?: string;
  language?: string;
  dateOfBirth?: any;
  insurance?: any;
  policyNumber?: any;
  telBusiness?: any;
  telPrivate?: any;
  email?: string;
  /**
   * **Deprecated:** Use the new [/api/change-attendee-password](#tag/Attendee/operation/changeAttendeePassword) API instead.
   */
  plainPassword?: any;
}

export type AttendeeJsonldRead = HydraItemBaseSchema & {
  id?: number;
  salutation?: number;
  title?: number;
  firstName: string;
  lastName: string;
  language?: string;
  dateOfBirth?: any;
  insurance: any;
  policyNumber?: any;
  telBusiness?: any;
  telPrivate?: any;
  email: string;
};

export interface AttendeeLogoutRequest {
  [key: string]: any;
}

export interface BookingReadAppointments {
  id?: number;
  campaign?: CampaignReadAppointments;
  appointment?: AppointmentReadAppointments;
  campaignSlug?: any;
}

export interface BookingWrite {
  appointment?: string;
  registrationData?: any[];
  reservationKey?: any;
}

export interface Campaign {
  id?: number;
  vtCampaignId?: any;
  name?: string;
  active?: boolean;
  organizer: any;
  company: Company | null;
  startDate?: string;
  endDate?: string;
  availableInEnglish?: boolean;
  maxAttendees?: any;
  vtExportAt?: number;
  vtExportExecutedAt?: any;
  teamStartDate?: any;
  teamEndDate?: any;
  campaignType?: CampaignType | null;
  registrationFields?: any;
  registrationEnableMarketingInfo?: boolean;
  appointmentsBookingStart?: any;
  appointmentsBookingEnd?: any;
  appointmentsBookingFields?: any[];
  appointmentsEnableMarketingInfo?: boolean;
  turnBookingsIntoAttendees?: boolean;
  appointmentsEnableWaitingList?: boolean;
  appointmentsEnableWaitingListOnZeroAppointments?: boolean;
  localisations?: {
    [key: string]: CampaignLocalisation;
  };
  attendees?: string[];
  teams?: string[];
  maxTeamMembers?: any;
  watchedBy?: string[];
  statistic?: any;
  mails?: string[];
  campaignMails?: CampaignMail[];
  teamInvitations?: string[];
  appointments?: string[];
  bookings?: string[];
  appointmentWaitingListEntries?: string[];
  contactHistories?: ContactHistory[];
  licenceCodes?: LicenceCode[];
  createdAt?: string;
  updatedAt?: string;
  createdBy?: any;
  updatedBy?: any;
  removedAttendees?: Collection;
  useCentralRegistration?: boolean;
  showLandingPage?: boolean;
  showTwoStartButtonsOnLandingPage?: boolean;
  keyVisual1x1?: any;
  keyVisual4x3?: any;
  portalData?: { [key: string]: any };
}

export interface CampaignReadAppointments {
  id?: number;
  organizer: OrganizerReadAppointments | null;
  availableInEnglish?: boolean;
  appointmentsBookingStart?: any;
  appointmentsBookingEnd?: any;
  appointmentsEnableMarketingInfo?: boolean;
  appointmentsEnableWaitingList?: boolean;
  appointmentsEnableWaitingListOnZeroAppointments?: boolean;
  localisations?: {
    [key: string]: CampaignLocalisationReadAppointments;
  };
  useCentralRegistration?: boolean;
  showTwoStartButtonsOnLandingPage?: boolean;
  keyVisual1x1?: ImageReadAppointments | null;
  keyVisual4x3?: ImageReadAppointments | null;
}

export interface CampaignReadPortal {
  id?: number;
  name?: string;
  startDate?: string;
  endDate?: string;
  maxTeamMembers?: any;
  portalData?: { [key: string]: any };
}

export interface CampaignAppointmentsRegistrationFieldsRead {
  fields?: any[];
}

export interface CampaignAttendeeApi {
  gaveDataConsent?: boolean;
  gaveAdApproval?: boolean;
  sendMails?: boolean;
  subjectToLicense?: boolean;
  campaignId?: any;
}

export interface CampaignAttendeeApiJsonMergePatch {
  gaveDataConsent?: boolean;
  gaveAdApproval?: boolean;
  sendMails?: boolean;
  subjectToLicense?: boolean;
  campaignId?: any;
}

export interface CampaignCollectionCampaignCollectionRead {
  organizer?: OrganizerCampaignCollectionRead | null;
  signaturePosition: 'no_signature' | 'below_opening_text' | 'below_closing_text';
  campaignCollectionRegisteredCampaigns?: CampaignCollectionRegisteredCampaignCampaignCollectionRead[];
  localisations?: {
    [key: string]: CampaignCollectionLocalisationCampaignCollectionRead;
  };
}

export interface CampaignCollectionLocalisationCampaignCollectionRead {
  title?: string;
  openingText?: any;
  closingText?: any;
}

export interface CampaignCollectionRegisteredCampaignCampaignCollectionRead {
  id?: number;
  linkUrl?: any;
  localisations?: {
    [key: string]: CampaignCollectionRegisteredCampaignLocalisationCampaignCollectionRead;
  };
}

export interface CampaignCollectionRegisteredCampaignLocalisationCampaignCollectionRead {
  campaignTitle?: string;
  teaserDescription?: any;
  importantInformation?: any;
}

export interface CampaignLocalisation {
  id?: number;
  campaign?: string;
  language?: string;
  additionalInformation?: any;
  landingPageTitle?: any;
  landingPageContent?: any;
  registrationMarketingInfoHeader?: any;
  registrationMarketingInfoLandingPage?: any;
  registrationMarketingInfoOverlay?: any;
  registrationThankYouPageAdditionalInformation?: any;
  appointmentsLandingPageTitle?: any;
  appointmentsLandingPageContent?: any;
  appointmentsMarketingInfoHeader?: any;
  appointmentsMarketingInfoLandingPage?: any;
  appointmentsMarketingInfoOverlay?: any;
  appointmentsThankYouPageAdditionalInformation?: any;
}

export interface CampaignLocalisationReadAppointments {
  appointmentsLandingPageTitle?: any;
  appointmentsLandingPageContent?: any;
  appointmentsMarketingInfoHeader?: any;
  appointmentsMarketingInfoLandingPage?: any;
  appointmentsMarketingInfoOverlay?: any;
  appointmentsThankYouPageAdditionalInformation?: any;
}

export interface CampaignMail {
  id?: number;
  campaign?: any;
  type?: string;
  mailTemplate: MailTemplate | null;
  limitMailRecipients?: LimitMailRecipients;
  localisations?: CampaignMailLocalisation[];
  createdAt?: string;
  createdBy?: any;
  mails?: Mail[];
}

export interface CampaignMailLocalisation {
  id?: number;
  campaignMail?: CampaignMail;
  language?: string;
  subject?: any;
  content?: any;
}

export interface CampaignRegistrationFieldsReadPortal {
  fields?: any[];
}

export interface CampaignStatistic {
  id?: number;
  campaign?: any;
  data?: any[];
}

export interface CampaignStatisticUpdateCampaignStatisticRequest {
  campaignId: number;
  data?: any[];
}

export interface CampaignType {
  id?: number;
  name?: string;
  enableVTExport?: boolean;
  isOffline?: boolean;
  enableForNewCampaigns?: boolean;
  enableLicenceCodes?: boolean;
  licenceCodeType?: string;
  keycloakClientId?: any;
  registrationLinkTemplate?: any;
  activationLinkTemplate?: any;
  passwordResetLinkTemplate?: any;
  loginLinkTemplate?: any;
  teamInvitationLinkTemplate?: any;
  contactReceivers?: any;
  keyVisual1x1?: any;
  keyVisual4x3?: any;
  activateAtmRegistrationPage?: boolean;
  showLandingPageBeforeRegistrationForm?: boolean;
  campaignsAvailableInEnglish?: boolean;
  showTwoStartButtonsOnLandingPage?: boolean;
  allowMultipleBookingsPerMail?: boolean;
  registrationFields?: any;
  appointmentsBookingFields?: any;
  useLaxGreeting?: boolean;
  additionalSendMailAllowList?: any;
  useCentralRegistration?: boolean;
  enableTwoPartTelephoneNumber?: boolean;
  enableStatisticsApis?: boolean;
  allowParallelLogins?: boolean;
  disallowDoubleLoginRegistration?: boolean;
  preventUserEnumeration?: boolean;
  requireCurrentPasswordForAttendeePasswordChange?: boolean;
  localisations?: {
    [key: string]: CampaignTypeLocalisation;
  };
  campaigns?: Campaign[];
  mailTemplates?: MailTemplate[];
  apiIsSecure?: boolean;
  statistic?: any;
  sites?: Site[];
  copyMailTemplatesFrom?: CampaignType | null;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: any;
  updatedBy?: any;
  userIdentifier?: string;
  username?: string;
  keyVisual1X1?: any;
  keyVisual4X3?: any;
  roles?: any[];
}

export interface CampaignTypeLocalisation {
  id?: number;
  campaignType?: CampaignType;
  language?: string;
  defaultLandingPageContent?: any;
  appointmentsDefaultLandingPageContent?: any;
}

export interface CampaignTypeStatistic {
  id?: number;
  campaignType?: CampaignType;
  data?: any[];
}

export interface CampaignTypeStatisticWrite {
  data?: any[];
}

export interface ChangeAttendeeEmail {
  /**
   * The current password of the attendee.
   */
  currentPassword: string;
  /**
   * The new email address.
   */
  newEmail: string;
}

export interface ChangeAttendeePassword {
  /**
   * The current password of the attendee.
   */
  currentPassword: string;
  /**
   * The new password. As plain text.
   */
  newPassword: string;
  /**
   * The new password, a second time, to prevent typos. Also as plain text.
   */
  newPasswordRepeated: string;
}

export interface Collection {
  empty?: {};
  keys?: {};
  values?: {};
  iterator?: {};
}

export interface Company {
  id?: number;
  vtCompanyId?: any;
  name?: string;
  addressOrigin?: any;
  registrationFields?: any;
  appointmentsBookingFields?: any;
  campaigns?: Campaign[];
  campaignsCanBeAccessedByAdminUsers?: Organizer[];
  registrationEnableMarketingInfo?: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: any;
  updatedBy?: any;
}

/**
 * Unprocessable entity
 */
export interface ConstraintViolation {
  status?: number;
  violations?: {
    /**
     * The property path of the violation
     */
    propertyPath: string;
    /**
     * The message associated with the violation
     */
    message: string;
    /**
     * The code of the violation
     */
    code?: string;
    /**
     * An extra hint to understand the violation
     */
    hint?: string;
    /**
     * The serialized payload of the violation
     */
    payload?: { [key: string]: any };
  }[];
  detail?: string;
  type?: string;
  title?: any;
  instance?: any;
}

export interface ContactHistory {
  id?: number;
  campaign?: any;
  attendeeId?: number;
  attendee?: any;
  deletedAttendeeData?: any;
  contactReason?: string;
  contactAt?: string;
  status?: string;
  wasIdentified?: boolean;
  errorMessage?: any;
  attendeeOrFullName?: {};
  fullName?: any;
  firstName?: any;
  lastName?: any;
  dateOfBirth?: any;
  policyNumber?: any;
}

export interface ContactRequest {
  receiver?: string;
  firstName: {};
  lastName: {};
  email: {};
  message: {};
}

/**
 * A representation of common errors.
 */
export interface Error {
  /**
   * A short, human-readable summary of the problem.
   */
  title?: any;
  /**
   * A human-readable explanation specific to this occurrence of the problem.
   */
  detail?: any;
  status?: any;
  /**
   * A URI reference that identifies the specific occurrence of the problem. It may or may not yield further information if dereferenced.
   */
  instance?: any;
  /**
   * A URI reference that identifies the problem type
   */
  type?: string;
}

export interface HydraItemBaseSchema {
  '@context'?:
    | string
    | {
        '@vocab': string;
        'hydra': 'http://www.w3.org/ns/hydra/core#';
        [key: string]: any;
      };
  '@id': string;
  '@type': string;
}

export interface ImageCampaignCollectionRead {
  blurhash?: string;
  contentUrl?: {};
}

export interface ImageReadAppointments {
  blurhash?: string;
  contentUrl?: {};
}

export interface InsuranceReadInsurance {
  id?: number;
  name?: string;
  policyNumberRequired?: boolean;
  inShortList?: boolean;
}

export interface JoinTeamRequestCreate {
  joinKey: {};
}

export interface JoinTeamRequestRead {
  campaignId?: {};
  invitedEmail?: {};
  isRegistered?: {};
}

export interface LicenceCode {
  id?: number;
  campaign?: any;
  attendee?: any;
  used?: boolean;
  code?: string;
  codeAsLink?: any;
  importedAt?: string;
  status?: 'free' | 'assigned' | 'abandoned';
}

export interface LimitMailRecipients {
  onlySbkInsured?: boolean;
  onlyPkvInsured?: boolean;
  onlyNotSbkAndPkvInsured?: boolean;
  limitByAdApproval?: number;
  limitByIsActivated?: number;
  limited?: boolean;
  limitedByInsurance?: boolean;
  onlySBKInsured?: boolean;
}

export interface Mail {
  id?: number;
  campaignAttendee?: any;
  campaign?: any;
  mailTemplate?: MailTemplate | null;
  campaignMail?: CampaignMail | null;
  booking?: any;
  attendee?: any;
  recipient?: any;
  subject?: any;
  greetingText?: any;
  mailContent?: any;
  sendAt?: string;
  createdAt?: string;
  status?: string;
  statusUpdatedAt?: string;
  hasPriority?: boolean;
  portalVars?: any[];
  episerverMailingToUserId?: any;
  sendLog?: MailSendLogCollection;
  errorMessage?: any;
}

export interface MailCreateMailRequest {
  attendeeIds: any[];
  portalTemplateId?: any;
  subject?: any;
  var1?: any;
  var2?: any;
  var3?: any;
  var4?: any;
  var5?: any;
  delay?: any;
}

export interface MailSendLog {
  isComplete?: boolean;
  recipient?: string;
  plannedToSendAt?: any;
  queuedAt?: any;
  processedAt?: any;
  sentToApiAt?: any;
  responseFromApiAt?: any;
  iteration?: number;
}

export interface MailSendLogCollection {
  latestSendLog?: MailSendLog | null;
}

export interface MailTemplate {
  id?: number;
  campaignType?: CampaignType | null;
  active?: boolean;
  episerverTemplateId?: string;
  episerverRecipientListId?: string;
  episerverSenderEmailPrefix?: string;
  episerverSenderName?: string;
  episerverTemplateParameters?: any;
  triggerType?: string;
  triggerSettings?: any;
  sendDelay?: number;
  limitMailRecipients?: LimitMailRecipients;
  localisations?: MailTemplateLocalisation[];
  mails?: Mail[];
  campaignMails?: CampaignMail[];
  createdAt?: string;
  updatedAt?: string;
  createdBy?: any;
  updatedBy?: any;
  identifierSubject?: string;
}

export interface MailTemplateLocalisation {
  id?: number;
  mailTemplate?: MailTemplate;
  language?: string;
  subject?: any;
  content?: any;
}

export interface Organizer {
  updatedAt?: string;
  updatedBy?: any;
  id?: number;
  firstName?: string;
  lastName?: string;
  sbkLoginName: any;
  roles?: string[];
  enabled?: boolean;
  portrait?: any;
  signature?: any;
  forceNewPassword?: boolean;
  sentInactivityWarningMail?: boolean;
  canAccessCampaignsOfCampaignTypes?: CampaignType[];
  watchedCampaigns?: string[];
  watchedCampaignCollections?: string[];
  campaigns?: string[];
  campaignCollections?: string[];
  email: string;
  password?: any;
  /**
   * **Deprecated:** Use the new [/api/change-attendee-password](#tag/Attendee/operation/changeAttendeePassword) API instead.
   */
  plainPassword?: any;
  lastLogin?: any;
  createdAt?: string;
  createdBy?: any;
  fullName?: any;
  adminRole?: string;
  userIdentifier?: string;
  username?: string;
}

export interface OrganizerCampaignCollectionRead {
  portrait?: ImageCampaignCollectionRead | null;
  signature?: any;
  fullName?: any;
}

export interface OrganizerReadAppointments {
  portrait?: ImageReadAppointments | null;
  signature?: any;
  fullName?: any;
}

export interface ResendActivationMailRequest {
  email: {};
}

export interface ResetPasswordRequest {
  campaignType?: {};
  email: {};
  key?: {};
  password?: {};
  purpose?: string;
}

export interface ResetPasswordRequestCheckPasswordResetKey {
  key: {};
}

export interface ResetPasswordRequestDoPasswordReset {
  key: {};
  password: {};
}

export interface ResetPasswordRequestRequestPasswordReset {
  email: {};
}

export interface Site {
  campaignType?: CampaignType | null;
  name?: string;
  position?: number;
  localisations?: {
    [key: string]: SiteLocalisation;
  };
  createdAt?: string;
  updatedAt?: string;
  createdBy?: any;
  updatedBy?: any;
  title?: any;
  content?: any;
}

export interface SiteReadCollection {
  name?: string;
  localisations?: {
    [key: string]: SiteLocalisationReadCollection;
  };
  title?: any;
}

export interface SiteReadItem {
  name?: string;
  localisations?: {
    [key: string]: SiteLocalisationReadItem;
  };
  title?: any;
  content?: any;
}

export interface SiteLocalisation {
  id?: number;
  site?: string;
  language?: string;
  title?: string;
  content?: string;
}

export interface SiteLocalisationReadCollection {
  title?: string;
}

export interface SiteLocalisationReadItem {
  title?: string;
  content?: string;
}

export interface TeamTeamCreate {
  name: string;
}

export interface TeamTeamName {
  name: string;
}

export interface TeamTeamRead {
  id?: number;
  name: string;
  /**
   * If true, the currently logged-in user is captain of this team and can manage it.
   */
  userIsCaptain?: boolean;
  members?: TeamMemberTeamRead[];
  invitations?: TeamInvitationTeamRead[];
  captainAttendeeId?: any;
  captainName?: any;
}

export interface TeamInvitationTeamRead {
  id?: number;
  anonymizedEmail?: string;
}

export interface TeamInvitationInviteToTeamRequest {
  emails?: {};
}

export interface TeamMemberTeamRead {
  id?: number;
  /**
   * If true, this team member is the currently logged-in user and can remove themself from the team.
   */
  isUser?: boolean;
  attendeeId?: any;
  fullName?: any;
}

export interface PostAppointmentsBookingsResponse {
  success?: boolean;
  /**
   * If true, the booking was only validated but not saved.
   */
  validateOnly?: boolean;
  /**
   * The amount of errors. Check the error property of the fields if this is
   * bigger than 0.
   */
  errorCount?: number;
  fields?: {};
}

export interface PostAppointmentsBookingsQueryParams {
  /**
   * The language to use for the registration form and its errors.
   */
  'language'?: string;
  /**
   * If true, the booking will be validated but not saved.
   */
  'validate-only'?: boolean;
}

export type PostAppointmentsBookingsProps = Omit<
  MutateProps<
    PostAppointmentsBookingsResponse,
    {} | Error | void | ConstraintViolation,
    PostAppointmentsBookingsQueryParams,
    BookingWrite,
    void
  >,
  'path' | 'verb'
>;

/**
 * Book an appointment
 *
 * Creates a new booking for the given appointment.
 *
 * The required fields for the registrationData can be retrieved from
 * `/api/campaigns/{id}/appointments-registration-fields`.
 */
export const PostAppointmentsBookings = (props: PostAppointmentsBookingsProps) => (
  <Mutate<
    PostAppointmentsBookingsResponse,
    {} | Error | void | ConstraintViolation,
    PostAppointmentsBookingsQueryParams,
    BookingWrite,
    void
  >
    verb="POST"
    path={`/api/appointments/bookings`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UsePostAppointmentsBookingsProps = Omit<
  UseMutateProps<
    PostAppointmentsBookingsResponse,
    {} | Error | void | ConstraintViolation,
    PostAppointmentsBookingsQueryParams,
    BookingWrite,
    void
  >,
  'path' | 'verb'
>;

/**
 * Book an appointment
 *
 * Creates a new booking for the given appointment.
 *
 * The required fields for the registrationData can be retrieved from
 * `/api/campaigns/{id}/appointments-registration-fields`.
 */
export const usePostAppointmentsBookings = (props: UsePostAppointmentsBookingsProps) =>
  useMutate<
    PostAppointmentsBookingsResponse,
    {} | Error | void | ConstraintViolation,
    PostAppointmentsBookingsQueryParams,
    BookingWrite,
    void
  >('POST', `/api/appointments/bookings`, {
    base: useAtmApiBaseUrl(),
    requestOptions: useAtmApiRequestOptions(false),
    ...props,
  });

export interface GetBookingForCancelPathParams {
  /**
   * Booking identifier
   */
  id: string;
  /**
   * Booking identifier
   */
  cancellationKey: string;
}

export type GetBookingForCancelProps = Omit<
  GetProps<BookingReadAppointments, void | Error, void, GetBookingForCancelPathParams>,
  'path'
> &
  GetBookingForCancelPathParams;

/**
 * Get a booking for review before cancelling it
 *
 * Gets a booking, to review it before cancelling.
 */
export const GetBookingForCancel = ({ id, cancellationKey, ...props }: GetBookingForCancelProps) => (
  <Get<BookingReadAppointments, void | Error, void, GetBookingForCancelPathParams>
    path={`/api/appointments/bookings/${id}/cancel/${cancellationKey}`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UseGetBookingForCancelProps = Omit<
  UseGetProps<BookingReadAppointments, void | Error, void, GetBookingForCancelPathParams>,
  'path'
> &
  GetBookingForCancelPathParams;

/**
 * Get a booking for review before cancelling it
 *
 * Gets a booking, to review it before cancelling.
 */
export const useGetBookingForCancel = ({ id, cancellationKey, ...props }: UseGetBookingForCancelProps) =>
  useGet<BookingReadAppointments, void | Error, void, GetBookingForCancelPathParams>(
    (paramsInPath: GetBookingForCancelPathParams) =>
      `/api/appointments/bookings/${paramsInPath.id}/cancel/${paramsInPath.cancellationKey}`,
    {
      base: useAtmApiBaseUrl(),
      requestOptions: useAtmApiRequestOptions(false),
      pathParams: { id, cancellationKey },
      ...props,
    },
  );

export interface CancelBookingPathParams {
  /**
   * Booking identifier
   */
  id: string;
}

export type CancelBookingProps = Omit<
  MutateProps<void, void | Error, void, string, CancelBookingPathParams>,
  'path' | 'verb'
> &
  CancelBookingPathParams;

/**
 * Cancel a booking.
 *
 * Cancels a booking with the given cancellation key.
 */
export const CancelBooking = ({ id, ...props }: CancelBookingProps) => (
  <Mutate<void, void | Error, void, string, CancelBookingPathParams>
    verb="DELETE"
    path={`/api/appointments/bookings/${id}/cancel`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UseCancelBookingProps = Omit<
  UseMutateProps<void, void | Error, void, string, CancelBookingPathParams>,
  'path' | 'verb'
> &
  CancelBookingPathParams;

/**
 * Cancel a booking.
 *
 * Cancels a booking with the given cancellation key.
 */
export const useCancelBooking = ({ id, ...props }: UseCancelBookingProps) =>
  useMutate<void, void | Error, void, string, CancelBookingPathParams>(
    'DELETE',
    (paramsInPath: CancelBookingPathParams) => `/api/appointments/bookings/${paramsInPath.id}/cancel`,
    { base: useAtmApiBaseUrl(), requestOptions: useAtmApiRequestOptions(false), pathParams: { id }, ...props },
  );

export type PostReserveAppointmentProps = Omit<
  MutateProps<AppointmentReservationRead, Error | void | ConstraintViolation, void, AppointmentReservationWrite, void>,
  'path' | 'verb'
>;

/**
 * Reserve an appointment
 *
 * Reserves an appointment for five minutes.
 *
 * Use the returned reservation key when finally booking the appointment. If the user switches to
 * another appointment, pass the old reservation key when making the new reservation to automatically
 * cancel the old reservation.
 */
export const PostReserveAppointment = (props: PostReserveAppointmentProps) => (
  <Mutate<AppointmentReservationRead, Error | void | ConstraintViolation, void, AppointmentReservationWrite, void>
    verb="POST"
    path={`/api/appointments/reservations`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UsePostReserveAppointmentProps = Omit<
  UseMutateProps<
    AppointmentReservationRead,
    Error | void | ConstraintViolation,
    void,
    AppointmentReservationWrite,
    void
  >,
  'path' | 'verb'
>;

/**
 * Reserve an appointment
 *
 * Reserves an appointment for five minutes.
 *
 * Use the returned reservation key when finally booking the appointment. If the user switches to
 * another appointment, pass the old reservation key when making the new reservation to automatically
 * cancel the old reservation.
 */
export const usePostReserveAppointment = (props: UsePostReserveAppointmentProps) =>
  useMutate<AppointmentReservationRead, Error | void | ConstraintViolation, void, AppointmentReservationWrite, void>(
    'POST',
    `/api/appointments/reservations`,
    { base: useAtmApiBaseUrl(), requestOptions: useAtmApiRequestOptions(false), ...props },
  );

export interface PostAppointmentsWaitingListEntriesResponse {
  success?: boolean;
  /**
   * If true, the waiting list entry was only validated but not saved.
   */
  validateOnly?: boolean;
  /**
   * The amount of errors. Check the error property of the fields if this is
   * bigger than 0.
   */
  errorCount?: number;
  fields?: {};
}

export interface PostAppointmentsWaitingListEntriesQueryParams {
  /**
   * The language to use for the registration form and its errors.
   */
  'language'?: string;
  /**
   * If true, the waiting list entry will be validated but not saved.
   */
  'validate-only'?: boolean;
}

export type PostAppointmentsWaitingListEntriesProps = Omit<
  MutateProps<
    PostAppointmentsWaitingListEntriesResponse,
    {} | Error | void | ConstraintViolation,
    PostAppointmentsWaitingListEntriesQueryParams,
    AppointmentWaitingListEntryWrite,
    void
  >,
  'path' | 'verb'
>;

/**
 * Add to waiting list
 *
 * If no fitting appointments are left, you can add the user to the waiting list of this campaign with
 * this method. The SBK can then manually contact the user and assign them a new appointment.
 *
 * The required fields for the registrationData can be retrieved from
 * `/api/campaigns/{id}/appointments-registration-fields`.
 */
export const PostAppointmentsWaitingListEntries = (props: PostAppointmentsWaitingListEntriesProps) => (
  <Mutate<
    PostAppointmentsWaitingListEntriesResponse,
    {} | Error | void | ConstraintViolation,
    PostAppointmentsWaitingListEntriesQueryParams,
    AppointmentWaitingListEntryWrite,
    void
  >
    verb="POST"
    path={`/api/appointments/waiting-list-entries`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UsePostAppointmentsWaitingListEntriesProps = Omit<
  UseMutateProps<
    PostAppointmentsWaitingListEntriesResponse,
    {} | Error | void | ConstraintViolation,
    PostAppointmentsWaitingListEntriesQueryParams,
    AppointmentWaitingListEntryWrite,
    void
  >,
  'path' | 'verb'
>;

/**
 * Add to waiting list
 *
 * If no fitting appointments are left, you can add the user to the waiting list of this campaign with
 * this method. The SBK can then manually contact the user and assign them a new appointment.
 *
 * The required fields for the registrationData can be retrieved from
 * `/api/campaigns/{id}/appointments-registration-fields`.
 */
export const usePostAppointmentsWaitingListEntries = (props: UsePostAppointmentsWaitingListEntriesProps) =>
  useMutate<
    PostAppointmentsWaitingListEntriesResponse,
    {} | Error | void | ConstraintViolation,
    PostAppointmentsWaitingListEntriesQueryParams,
    AppointmentWaitingListEntryWrite,
    void
  >('POST', `/api/appointments/waiting-list-entries`, {
    base: useAtmApiBaseUrl(),
    requestOptions: useAtmApiRequestOptions(false),
    ...props,
  });

export interface GetCampaignQueryParams {
  context?: string;
}

export interface GetCampaignPathParams {
  /**
   * Campaign identifier
   */
  id: string;
}

export type GetCampaignProps = Omit<
  GetProps<CampaignReadPortal, Error, GetCampaignQueryParams, GetCampaignPathParams>,
  'path'
> &
  GetCampaignPathParams;

/**
 * Get a single campaign
 *
 * Returns a single campaign.
 */
export const GetCampaign = ({ id, ...props }: GetCampaignProps) => (
  <Get<CampaignReadPortal, Error, GetCampaignQueryParams, GetCampaignPathParams>
    path={`/api/campaigns/${id}`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UseGetCampaignProps = Omit<
  UseGetProps<CampaignReadPortal, Error, GetCampaignQueryParams, GetCampaignPathParams>,
  'path'
> &
  GetCampaignPathParams;

/**
 * Get a single campaign
 *
 * Returns a single campaign.
 */
export const useGetCampaign = ({ id, ...props }: UseGetCampaignProps) =>
  useGet<CampaignReadPortal, Error, GetCampaignQueryParams, GetCampaignPathParams>(
    (paramsInPath: GetCampaignPathParams) => `/api/campaigns/${paramsInPath.id}`,
    { base: useAtmApiBaseUrl(), requestOptions: useAtmApiRequestOptions(false), pathParams: { id }, ...props },
  );

export interface GetCampaignAppointmentsPathParams {
  /**
   * Appointment identifier
   */
  id: string;
}

export type GetCampaignAppointmentsProps = Omit<
  GetProps<AppointmentReadReadAppointments[], Error, void, GetCampaignAppointmentsPathParams>,
  'path'
> &
  GetCampaignAppointmentsPathParams;

/**
 * List all appointments for a campaign.
 *
 * Returns a list of all appointments for the specified campaign.
 */
export const GetCampaignAppointments = ({ id, ...props }: GetCampaignAppointmentsProps) => (
  <Get<AppointmentReadReadAppointments[], Error, void, GetCampaignAppointmentsPathParams>
    path={`/api/campaigns/${id}/appointments`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UseGetCampaignAppointmentsProps = Omit<
  UseGetProps<AppointmentReadReadAppointments[], Error, void, GetCampaignAppointmentsPathParams>,
  'path'
> &
  GetCampaignAppointmentsPathParams;

/**
 * List all appointments for a campaign.
 *
 * Returns a list of all appointments for the specified campaign.
 */
export const useGetCampaignAppointments = ({ id, ...props }: UseGetCampaignAppointmentsProps) =>
  useGet<AppointmentReadReadAppointments[], Error, void, GetCampaignAppointmentsPathParams>(
    (paramsInPath: GetCampaignAppointmentsPathParams) => `/api/campaigns/${paramsInPath.id}/appointments`,
    { base: useAtmApiBaseUrl(), requestOptions: useAtmApiRequestOptions(false), pathParams: { id }, ...props },
  );

export interface GetCampaignAppointmentsRegistrationFieldsResponse {
  fields?: {};
}

export interface GetCampaignAppointmentsRegistrationFieldsPathParams {
  /**
   * CampaignAppointmentsRegistrationFields identifier
   */
  id: string;
}

export type GetCampaignAppointmentsRegistrationFieldsProps = Omit<
  GetProps<
    GetCampaignAppointmentsRegistrationFieldsResponse,
    Error | void,
    void,
    GetCampaignAppointmentsRegistrationFieldsPathParams
  >,
  'path'
> &
  GetCampaignAppointmentsRegistrationFieldsPathParams;

/**
 * Get appointment registration fields for a campaign
 *
 * Returns the fields that are required to register to book an appointment for a given campaign.
 */
export const GetCampaignAppointmentsRegistrationFields = ({
  id,
  ...props
}: GetCampaignAppointmentsRegistrationFieldsProps) => (
  <Get<
    GetCampaignAppointmentsRegistrationFieldsResponse,
    Error | void,
    void,
    GetCampaignAppointmentsRegistrationFieldsPathParams
  >
    path={`/api/campaigns/${id}/appointments-registration-fields`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UseGetCampaignAppointmentsRegistrationFieldsProps = Omit<
  UseGetProps<
    GetCampaignAppointmentsRegistrationFieldsResponse,
    Error | void,
    void,
    GetCampaignAppointmentsRegistrationFieldsPathParams
  >,
  'path'
> &
  GetCampaignAppointmentsRegistrationFieldsPathParams;

/**
 * Get appointment registration fields for a campaign
 *
 * Returns the fields that are required to register to book an appointment for a given campaign.
 */
export const useGetCampaignAppointmentsRegistrationFields = ({
  id,
  ...props
}: UseGetCampaignAppointmentsRegistrationFieldsProps) =>
  useGet<
    GetCampaignAppointmentsRegistrationFieldsResponse,
    Error | void,
    void,
    GetCampaignAppointmentsRegistrationFieldsPathParams
  >(
    (paramsInPath: GetCampaignAppointmentsRegistrationFieldsPathParams) =>
      `/api/campaigns/${paramsInPath.id}/appointments-registration-fields`,
    { base: useAtmApiBaseUrl(), requestOptions: useAtmApiRequestOptions(false), pathParams: { id }, ...props },
  );

export interface PostLoginResponse {
  /**
   * The generated token to access the private api.
   */
  token?: string;
  /**
   * After how many seconds of inactivity the token expires. Calling a
   * private api with the token resets this timer.
   */
  attendeeId?: number;
}

export interface PostLoginRequestBody {
  /**
   *  The email address of the attendee you want to login.
   */
  email: string;
  /**
   * The password of the attendee you want to login.
   */
  password: string;
  campaignId?: number;
  /**
   * If set, required to be true
   */
  dataConsent?: boolean;
  adApproval?: boolean;
}

export type PostLoginProps = Omit<
  MutateProps<
    PostLoginResponse,
    | {}
    | {
        code?: number;
        message?: string;
      }
    | Error
    | ConstraintViolation,
    void,
    PostLoginRequestBody,
    void
  >,
  'path' | 'verb'
>;

/**
 * Login as attendee
 *
 * Generates a token to access the private api.
 *
 * Returns a token that can be used to access the private api. For example to retrieve and update the
 * saved data of an attendee for your portal.
 *
 * The token expires one hour after its last usage so the user gets automatically logged out.
 * If the user logs out through another portal, all login tokens are invalidated.
 *
 * ------------
 *
 * This method also gets used to register already existing attendees to new campaigns.
 * If the registration form says that the provided email already exists, the user can use the login
 * form to register on the campaign with their existing user credentials.
 * Then, instead of returning an error that the attendee is not registered for any campaign of your
 * portal, the login will automatically register the attendee on the provided campaign and then
 * proceed with the normal login.
 *
 * It is possible for the SBK to add dynamic registration fields to the login form, so you first need
 * to retrieve the form fields required for the login from the
 * `/api/campaigns/{id}/register?forLogin=true` API.
 * The `forLogin=true` will result in the API only returning the fields required for the login.
 *
 * In addition to the fields returned by the api, **you also have to send the campaign id** to the
 * login api** (see request format), otherwise the login api will treat the request as a normal login
 * request.
 *
 * **Note:** On success, the login API will return an error that the user is not activated yet. **This
 * means the registration was successfull!**
 * This is because after the registration process, the login API procceeds with the normal login
 * checks, which then triggers the "Not activated yet" error.
 */
export const PostLogin = (props: PostLoginProps) => (
  <Mutate<
    PostLoginResponse,
    | {}
    | {
        code?: number;
        message?: string;
      }
    | Error
    | ConstraintViolation,
    void,
    PostLoginRequestBody,
    void
  >
    verb="POST"
    path={`/api/login`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UsePostLoginProps = Omit<
  UseMutateProps<
    PostLoginResponse,
    | {}
    | {
        code?: number;
        message?: string;
      }
    | Error
    | ConstraintViolation,
    void,
    PostLoginRequestBody,
    void
  >,
  'path' | 'verb'
>;

/**
 * Login as attendee
 *
 * Generates a token to access the private api.
 *
 * Returns a token that can be used to access the private api. For example to retrieve and update the
 * saved data of an attendee for your portal.
 *
 * The token expires one hour after its last usage so the user gets automatically logged out.
 * If the user logs out through another portal, all login tokens are invalidated.
 *
 * ------------
 *
 * This method also gets used to register already existing attendees to new campaigns.
 * If the registration form says that the provided email already exists, the user can use the login
 * form to register on the campaign with their existing user credentials.
 * Then, instead of returning an error that the attendee is not registered for any campaign of your
 * portal, the login will automatically register the attendee on the provided campaign and then
 * proceed with the normal login.
 *
 * It is possible for the SBK to add dynamic registration fields to the login form, so you first need
 * to retrieve the form fields required for the login from the
 * `/api/campaigns/{id}/register?forLogin=true` API.
 * The `forLogin=true` will result in the API only returning the fields required for the login.
 *
 * In addition to the fields returned by the api, **you also have to send the campaign id** to the
 * login api** (see request format), otherwise the login api will treat the request as a normal login
 * request.
 *
 * **Note:** On success, the login API will return an error that the user is not activated yet. **This
 * means the registration was successfull!**
 * This is because after the registration process, the login API procceeds with the normal login
 * checks, which then triggers the "Not activated yet" error.
 */
export const usePostLogin = (props: UsePostLoginProps) =>
  useMutate<
    PostLoginResponse,
    | {}
    | {
        code?: number;
        message?: string;
      }
    | Error
    | ConstraintViolation,
    void,
    PostLoginRequestBody,
    void
  >('POST', `/api/login`, { base: useAtmApiBaseUrl(), requestOptions: useAtmApiRequestOptions(false), ...props });

export interface PostRequestPasswordResetResponse {
  success?: boolean;
}

export type PostRequestPasswordResetProps = Omit<
  MutateProps<
    PostRequestPasswordResetResponse,
    {} | Error | ConstraintViolation,
    void,
    ResetPasswordRequestRequestPasswordReset,
    void
  >,
  'path' | 'verb'
>;

/**
 * Start the password reset process
 *
 * Starts the password reset process for an attendee.
 *
 * Sends a mail to the provided attendee email with a password reset link.
 * The link will point to your portal and contains a key that must be passed to the
 * *[/reset-password/set](#tag/Attendee-Login/operation/postDoPasswordReset)* method. The key is
 * urlencoded.
 *
 * The link format can be set by the SBK through the ATM backend.
 */
export const PostRequestPasswordReset = (props: PostRequestPasswordResetProps) => (
  <Mutate<
    PostRequestPasswordResetResponse,
    {} | Error | ConstraintViolation,
    void,
    ResetPasswordRequestRequestPasswordReset,
    void
  >
    verb="POST"
    path={`/api/reset-password`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UsePostRequestPasswordResetProps = Omit<
  UseMutateProps<
    PostRequestPasswordResetResponse,
    {} | Error | ConstraintViolation,
    void,
    ResetPasswordRequestRequestPasswordReset,
    void
  >,
  'path' | 'verb'
>;

/**
 * Start the password reset process
 *
 * Starts the password reset process for an attendee.
 *
 * Sends a mail to the provided attendee email with a password reset link.
 * The link will point to your portal and contains a key that must be passed to the
 * *[/reset-password/set](#tag/Attendee-Login/operation/postDoPasswordReset)* method. The key is
 * urlencoded.
 *
 * The link format can be set by the SBK through the ATM backend.
 */
export const usePostRequestPasswordReset = (props: UsePostRequestPasswordResetProps) =>
  useMutate<
    PostRequestPasswordResetResponse,
    {} | Error | ConstraintViolation,
    void,
    ResetPasswordRequestRequestPasswordReset,
    void
  >('POST', `/api/reset-password`, {
    base: useAtmApiBaseUrl(),
    requestOptions: useAtmApiRequestOptions(false),
    ...props,
  });

export interface PostCheckPasswordResetKeyResponse {
  success?: boolean;
}

export type PostCheckPasswordResetKeyProps = Omit<
  MutateProps<
    PostCheckPasswordResetKeyResponse,
    {} | Error | ConstraintViolation,
    void,
    ResetPasswordRequestCheckPasswordResetKey,
    void
  >,
  'path' | 'verb'
>;

/**
 * Check the password reset key
 *
 * Checks if a password reset key is valid.
 *
 * Requires the key generated by
 * *[/api/reset-password](#tag/Attendee-Login/operation/postRequestPasswordReset)*.
 */
export const PostCheckPasswordResetKey = (props: PostCheckPasswordResetKeyProps) => (
  <Mutate<
    PostCheckPasswordResetKeyResponse,
    {} | Error | ConstraintViolation,
    void,
    ResetPasswordRequestCheckPasswordResetKey,
    void
  >
    verb="POST"
    path={`/api/reset-password/check`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UsePostCheckPasswordResetKeyProps = Omit<
  UseMutateProps<
    PostCheckPasswordResetKeyResponse,
    {} | Error | ConstraintViolation,
    void,
    ResetPasswordRequestCheckPasswordResetKey,
    void
  >,
  'path' | 'verb'
>;

/**
 * Check the password reset key
 *
 * Checks if a password reset key is valid.
 *
 * Requires the key generated by
 * *[/api/reset-password](#tag/Attendee-Login/operation/postRequestPasswordReset)*.
 */
export const usePostCheckPasswordResetKey = (props: UsePostCheckPasswordResetKeyProps) =>
  useMutate<
    PostCheckPasswordResetKeyResponse,
    {} | Error | ConstraintViolation,
    void,
    ResetPasswordRequestCheckPasswordResetKey,
    void
  >('POST', `/api/reset-password/check`, {
    base: useAtmApiBaseUrl(),
    requestOptions: useAtmApiRequestOptions(false),
    ...props,
  });

export interface PostDoPasswordResetResponse {
  success?: boolean;
}

export type PostDoPasswordResetProps = Omit<
  MutateProps<
    PostDoPasswordResetResponse,
    {} | Error | ConstraintViolation,
    void,
    ResetPasswordRequestDoPasswordReset,
    void
  >,
  'path' | 'verb'
>;

/**
 * Set the new attendee password
 *
 * Sets a new password for an attendee.
 *
 * Requires the key generated by
 * *[/api/reset-password](#tag/Attendee-Login/operation/postRequestPasswordReset)*.
 */
export const PostDoPasswordReset = (props: PostDoPasswordResetProps) => (
  <Mutate<
    PostDoPasswordResetResponse,
    {} | Error | ConstraintViolation,
    void,
    ResetPasswordRequestDoPasswordReset,
    void
  >
    verb="POST"
    path={`/api/reset-password/set`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UsePostDoPasswordResetProps = Omit<
  UseMutateProps<
    PostDoPasswordResetResponse,
    {} | Error | ConstraintViolation,
    void,
    ResetPasswordRequestDoPasswordReset,
    void
  >,
  'path' | 'verb'
>;

/**
 * Set the new attendee password
 *
 * Sets a new password for an attendee.
 *
 * Requires the key generated by
 * *[/api/reset-password](#tag/Attendee-Login/operation/postRequestPasswordReset)*.
 */
export const usePostDoPasswordReset = (props: UsePostDoPasswordResetProps) =>
  useMutate<
    PostDoPasswordResetResponse,
    {} | Error | ConstraintViolation,
    void,
    ResetPasswordRequestDoPasswordReset,
    void
  >('POST', `/api/reset-password/set`, {
    base: useAtmApiBaseUrl(),
    requestOptions: useAtmApiRequestOptions(false),
    ...props,
  });

export interface GetAttendeeIdsQueryParams {
  /**
   * The collection page number
   */
  page?: number;
  /**
   * The result can be limited to a specific campaign by passing the `campaignId` as query
   * parameter, e.g. `/api/attendee-ids?campaignId=123`.
   * It is also possible to return attendee ids from multiple campaigns by passing a list of
   * campaign ids in the form of of `/api/attendee-ids?campaignId=123,456,789,...`.
   */
  campaignId?: string;
}

export type GetAttendeeIdsProps = Omit<
  GetProps<AttendeeAttendeeReadId[], Error, GetAttendeeIdsQueryParams, void>,
  'path'
>;

/**
 * Get all attendee IDs for your portal
 *
 * Returns a list of attendee IDs that are currently registered with one of the campaigns of your
 * portal.
 *
 * The result will be paginated. To retrieve all attendee IDs, you have to call the method multiple
 * times with an increasing `page` number.
 * The limit is 1000 items per page. Once you get less than 1000 items, you reached the end of the list
 * and can stop calling the api.
 */
export const GetAttendeeIds = (props: GetAttendeeIdsProps) => (
  <Get<AttendeeAttendeeReadId[], Error, GetAttendeeIdsQueryParams, void>
    path={`/api/attendee-ids`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UseGetAttendeeIdsProps = Omit<
  UseGetProps<AttendeeAttendeeReadId[], Error, GetAttendeeIdsQueryParams, void>,
  'path'
>;

/**
 * Get all attendee IDs for your portal
 *
 * Returns a list of attendee IDs that are currently registered with one of the campaigns of your
 * portal.
 *
 * The result will be paginated. To retrieve all attendee IDs, you have to call the method multiple
 * times with an increasing `page` number.
 * The limit is 1000 items per page. Once you get less than 1000 items, you reached the end of the list
 * and can stop calling the api.
 */
export const useGetAttendeeIds = (props: UseGetAttendeeIdsProps) =>
  useGet<AttendeeAttendeeReadId[], Error, GetAttendeeIdsQueryParams, void>(`/api/attendee-ids`, {
    base: useAtmApiBaseUrl(),
    requestOptions: useAtmApiRequestOptions(false),
    ...props,
  });

export interface GetCampaignCollectionPathParams {
  /**
   * CampaignCollection identifier
   */
  id: string;
}

export type GetCampaignCollectionProps = Omit<
  GetProps<CampaignCollectionCampaignCollectionRead, Error, void, GetCampaignCollectionPathParams>,
  'path'
> &
  GetCampaignCollectionPathParams;

/**
 * Get a single campaign collection
 *
 * Returns the campaign collection for the given id.
 */
export const GetCampaignCollection = ({ id, ...props }: GetCampaignCollectionProps) => (
  <Get<CampaignCollectionCampaignCollectionRead, Error, void, GetCampaignCollectionPathParams>
    path={`/api/campaign-collections/${id}`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UseGetCampaignCollectionProps = Omit<
  UseGetProps<CampaignCollectionCampaignCollectionRead, Error, void, GetCampaignCollectionPathParams>,
  'path'
> &
  GetCampaignCollectionPathParams;

/**
 * Get a single campaign collection
 *
 * Returns the campaign collection for the given id.
 */
export const useGetCampaignCollection = ({ id, ...props }: UseGetCampaignCollectionProps) =>
  useGet<CampaignCollectionCampaignCollectionRead, Error, void, GetCampaignCollectionPathParams>(
    (paramsInPath: GetCampaignCollectionPathParams) => `/api/campaign-collections/${paramsInPath.id}`,
    { base: useAtmApiBaseUrl(), requestOptions: useAtmApiRequestOptions(false), pathParams: { id }, ...props },
  );

export interface PostActivateAttendeeResponse {
  success?: boolean;
}

export type PostActivateAttendeeProps = Omit<
  MutateProps<PostActivateAttendeeResponse, {} | Error | ConstraintViolation, void, ActivateAttendeeRequest, void>,
  'path' | 'verb'
>;

/**
 * Activate attendee for campaign
 *
 * Activates an attendee for a campaign.
 *
 * Requires the activation key send with the mail after a successfull registration.
 *
 * You should first only send the activation key. If this returns 200 the attendee is activated and
 * you can redirect them to the login.
 * If a 400 Bad Request is returned, together with the "Please provide a new password." error, you
 * should prompt the user for a new password (and to repeat this password) and then re-send the
 * activation request with the password chosen by the user.
 */
export const PostActivateAttendee = (props: PostActivateAttendeeProps) => (
  <Mutate<PostActivateAttendeeResponse, {} | Error | ConstraintViolation, void, ActivateAttendeeRequest, void>
    verb="POST"
    path={`/api/activate-attendee`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UsePostActivateAttendeeProps = Omit<
  UseMutateProps<PostActivateAttendeeResponse, {} | Error | ConstraintViolation, void, ActivateAttendeeRequest, void>,
  'path' | 'verb'
>;

/**
 * Activate attendee for campaign
 *
 * Activates an attendee for a campaign.
 *
 * Requires the activation key send with the mail after a successfull registration.
 *
 * You should first only send the activation key. If this returns 200 the attendee is activated and
 * you can redirect them to the login.
 * If a 400 Bad Request is returned, together with the "Please provide a new password." error, you
 * should prompt the user for a new password (and to repeat this password) and then re-send the
 * activation request with the password chosen by the user.
 */
export const usePostActivateAttendee = (props: UsePostActivateAttendeeProps) =>
  useMutate<PostActivateAttendeeResponse, {} | Error | ConstraintViolation, void, ActivateAttendeeRequest, void>(
    'POST',
    `/api/activate-attendee`,
    { base: useAtmApiBaseUrl(), requestOptions: useAtmApiRequestOptions(false), ...props },
  );

export interface GetCampaignRegisterResponse {
  fields?: {};
}

export interface GetCampaignRegisterQueryParams {
  context: ('portal' | 'registration-frontend')[];
  /**
   * If true, only the fields for the registration via login form will be returned.
   */
  forLogin?: boolean;
  /**
   * If your portal supports more than one language, provide the language selected by the user
   * here. This language will then be used for translating the error messages and as language
   * preference for the mails sent to the user. Currently only 'de' and 'en' are available,
   * other values are ignored. Default is \'en\'',
   */
  language?: string;
}

export interface GetCampaignRegisterPathParams {
  /**
   * The id of the campaign
   */
  id: string;
}

export type GetCampaignRegisterProps = Omit<
  GetProps<
    GetCampaignRegisterResponse,
    {} | void | Error,
    GetCampaignRegisterQueryParams,
    GetCampaignRegisterPathParams
  >,
  'path'
> &
  GetCampaignRegisterPathParams;

/**
 * Get registration fields for a campaign
 *
 * Returns the fields that are required to register for a given campaign.
 */
export const GetCampaignRegister = ({ id, ...props }: GetCampaignRegisterProps) => (
  <Get<GetCampaignRegisterResponse, {} | void | Error, GetCampaignRegisterQueryParams, GetCampaignRegisterPathParams>
    path={`/api/campaigns/${id}/register`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UseGetCampaignRegisterProps = Omit<
  UseGetProps<
    GetCampaignRegisterResponse,
    {} | void | Error,
    GetCampaignRegisterQueryParams,
    GetCampaignRegisterPathParams
  >,
  'path'
> &
  GetCampaignRegisterPathParams;

/**
 * Get registration fields for a campaign
 *
 * Returns the fields that are required to register for a given campaign.
 */
export const useGetCampaignRegister = ({ id, ...props }: UseGetCampaignRegisterProps) =>
  useGet<GetCampaignRegisterResponse, {} | void | Error, GetCampaignRegisterQueryParams, GetCampaignRegisterPathParams>(
    (paramsInPath: GetCampaignRegisterPathParams) => `/api/campaigns/${paramsInPath.id}/register`,
    { base: useAtmApiBaseUrl(), requestOptions: useAtmApiRequestOptions(false), pathParams: { id }, ...props },
  );

export interface PostCampaignRegisterResponse {
  success?: boolean;
  /**
   * If true, the registration was only validated but not saved.
   */
  validateOnly?: boolean;
  /**
   * The amount of errors. Check the error property of the fields if this is bigger than 0.
   */
  errorCount?: number;
  fields?: {};
}

export interface PostCampaignRegisterQueryParams {
  'context': ('portal' | 'registration-frontend')[];
  /**
   * If your portal supports more than one language, provide the language selected by the user
   * here. This language will then be used for translating the error messages and as language
   * preference for the mails sent to the user. Currently only 'de' and 'en' are available,
   * other values are ignored. Default is \'en\'',
   */
  'language'?: string;
  /**
   * If true, the registration will be validated but not saved. This is useful if you want to
   * validate the registration data before actually registering the attendee. E.g if you have a
   * review step before the final registration and want to validate the data before proceeding
   * to the review step.',
   */
  'validate-only'?: string;
}

export interface PostCampaignRegisterPathParams {
  /**
   * The id of the campaign
   */
  id: string;
}

export interface PostCampaignRegisterRequestBody {
  [key: string]: any;
}

export type PostCampaignRegisterProps = Omit<
  MutateProps<
    PostCampaignRegisterResponse,
    {} | void | Error | ConstraintViolation,
    PostCampaignRegisterQueryParams,
    PostCampaignRegisterRequestBody,
    PostCampaignRegisterPathParams
  >,
  'path' | 'verb'
> &
  PostCampaignRegisterPathParams;

/**
 * Register an attendee for a campaign.
 *
 * If the attendee doesn't exist yet, it will be created.
 */
export const PostCampaignRegister = ({ id, ...props }: PostCampaignRegisterProps) => (
  <Mutate<
    PostCampaignRegisterResponse,
    {} | void | Error | ConstraintViolation,
    PostCampaignRegisterQueryParams,
    PostCampaignRegisterRequestBody,
    PostCampaignRegisterPathParams
  >
    verb="POST"
    path={`/api/campaigns/${id}/register`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UsePostCampaignRegisterProps = Omit<
  UseMutateProps<
    PostCampaignRegisterResponse,
    {} | void | Error | ConstraintViolation,
    PostCampaignRegisterQueryParams,
    PostCampaignRegisterRequestBody,
    PostCampaignRegisterPathParams
  >,
  'path' | 'verb'
> &
  PostCampaignRegisterPathParams;

/**
 * Register an attendee for a campaign.
 *
 * If the attendee doesn't exist yet, it will be created.
 */
export const usePostCampaignRegister = ({ id, ...props }: UsePostCampaignRegisterProps) =>
  useMutate<
    PostCampaignRegisterResponse,
    {} | void | Error | ConstraintViolation,
    PostCampaignRegisterQueryParams,
    PostCampaignRegisterRequestBody,
    PostCampaignRegisterPathParams
  >('POST', (paramsInPath: PostCampaignRegisterPathParams) => `/api/campaigns/${paramsInPath.id}/register`, {
    base: useAtmApiBaseUrl(),
    requestOptions: useAtmApiRequestOptions(false),
    pathParams: { id },
    ...props,
  });

export interface PostResendActivationMailResponse {
  success?: boolean;
}

export type PostResendActivationMailProps = Omit<
  MutateProps<
    PostResendActivationMailResponse,
    {} | Error | ConstraintViolation,
    void,
    ResendActivationMailRequest,
    void
  >,
  'path' | 'verb'
>;

/**
 * Resend activation mail to attendee
 *
 * Resends the activation mail, which contains the activation key, to the attendee with the given
 * email address.
 *
 * This is needed if the attendee did not receive the original activation mail or the activation key
 * has expired.
 */
export const PostResendActivationMail = (props: PostResendActivationMailProps) => (
  <Mutate<PostResendActivationMailResponse, {} | Error | ConstraintViolation, void, ResendActivationMailRequest, void>
    verb="POST"
    path={`/api/resend-activation-mail`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UsePostResendActivationMailProps = Omit<
  UseMutateProps<
    PostResendActivationMailResponse,
    {} | Error | ConstraintViolation,
    void,
    ResendActivationMailRequest,
    void
  >,
  'path' | 'verb'
>;

/**
 * Resend activation mail to attendee
 *
 * Resends the activation mail, which contains the activation key, to the attendee with the given
 * email address.
 *
 * This is needed if the attendee did not receive the original activation mail or the activation key
 * has expired.
 */
export const usePostResendActivationMail = (props: UsePostResendActivationMailProps) =>
  useMutate<
    PostResendActivationMailResponse,
    {} | Error | ConstraintViolation,
    void,
    ResendActivationMailRequest,
    void
  >('POST', `/api/resend-activation-mail`, {
    base: useAtmApiBaseUrl(),
    requestOptions: useAtmApiRequestOptions(false),
    ...props,
  });

export interface GetCampaignsQueryParams {
  context?: string;
}

export type GetCampaignsProps = Omit<GetProps<CampaignReadPortal[], Error, GetCampaignsQueryParams, void>, 'path'>;

/**
 * List all campaigns in your portal
 *
 * Returns a list of all campaigns in your portal.
 */
export const GetCampaigns = (props: GetCampaignsProps) => (
  <Get<CampaignReadPortal[], Error, GetCampaignsQueryParams, void>
    path={`/api/campaigns`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UseGetCampaignsProps = Omit<
  UseGetProps<CampaignReadPortal[], Error, GetCampaignsQueryParams, void>,
  'path'
>;

/**
 * List all campaigns in your portal
 *
 * Returns a list of all campaigns in your portal.
 */
export const useGetCampaigns = (props: UseGetCampaignsProps) =>
  useGet<CampaignReadPortal[], Error, GetCampaignsQueryParams, void>(`/api/campaigns`, {
    base: useAtmApiBaseUrl(),
    requestOptions: useAtmApiRequestOptions(false),
    ...props,
  });

export interface PostContactResponse {
  success?: boolean;
}

export type PostContactProps = Omit<
  MutateProps<PostContactResponse, {} | Error | ConstraintViolation, void, ContactRequest, void>,
  'path' | 'verb'
>;

/**
 * Send a contact request via mail
 *
 * Creates a contact request. The request will be sent to the selected receiver.
 *
 * The receiver can be set via the `receiver` field. If not set, the "default" receiver will be used.
 */
export const PostContact = (props: PostContactProps) => (
  <Mutate<PostContactResponse, {} | Error | ConstraintViolation, void, ContactRequest, void>
    verb="POST"
    path={`/api/contact`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UsePostContactProps = Omit<
  UseMutateProps<PostContactResponse, {} | Error | ConstraintViolation, void, ContactRequest, void>,
  'path' | 'verb'
>;

/**
 * Send a contact request via mail
 *
 * Creates a contact request. The request will be sent to the selected receiver.
 *
 * The receiver can be set via the `receiver` field. If not set, the "default" receiver will be used.
 */
export const usePostContact = (props: UsePostContactProps) =>
  useMutate<PostContactResponse, {} | Error | ConstraintViolation, void, ContactRequest, void>('POST', `/api/contact`, {
    base: useAtmApiBaseUrl(),
    requestOptions: useAtmApiRequestOptions(false),
    ...props,
  });

export type GetInsurancesProps = Omit<GetProps<InsuranceReadInsurance[], Error, void, void>, 'path'>;

/**
 * Get all insurance companies
 *
 * Returns a list of all insurance companies.
 */
export const GetInsurances = (props: GetInsurancesProps) => (
  <Get<InsuranceReadInsurance[], Error, void, void>
    path={`/api/insurances`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UseGetInsurancesProps = Omit<UseGetProps<InsuranceReadInsurance[], Error, void, void>, 'path'>;

/**
 * Get all insurance companies
 *
 * Returns a list of all insurance companies.
 */
export const useGetInsurances = (props: UseGetInsurancesProps) =>
  useGet<InsuranceReadInsurance[], Error, void, void>(`/api/insurances`, {
    base: useAtmApiBaseUrl(),
    requestOptions: useAtmApiRequestOptions(false),
    ...props,
  });

export interface GetInsurancePathParams {
  /**
   * Insurance identifier
   */
  id: string;
}

export type GetInsuranceProps = Omit<GetProps<InsuranceReadInsurance, Error, void, GetInsurancePathParams>, 'path'> &
  GetInsurancePathParams;

/**
 * Get a single insurance company
 *
 * Returns the insurance company for the given id.
 */
export const GetInsurance = ({ id, ...props }: GetInsuranceProps) => (
  <Get<InsuranceReadInsurance, Error, void, GetInsurancePathParams>
    path={`/api/insurances/${id}`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UseGetInsuranceProps = Omit<
  UseGetProps<InsuranceReadInsurance, Error, void, GetInsurancePathParams>,
  'path'
> &
  GetInsurancePathParams;

/**
 * Get a single insurance company
 *
 * Returns the insurance company for the given id.
 */
export const useGetInsurance = ({ id, ...props }: UseGetInsuranceProps) =>
  useGet<InsuranceReadInsurance, Error, void, GetInsurancePathParams>(
    (paramsInPath: GetInsurancePathParams) => `/api/insurances/${paramsInPath.id}`,
    { base: useAtmApiBaseUrl(), requestOptions: useAtmApiRequestOptions(false), pathParams: { id }, ...props },
  );

export interface GetJoinTeamPathParams {
  /**
   * JoinTeamRequest identifier
   */
  joinKey: string;
}

export type GetJoinTeamProps = Omit<GetProps<JoinTeamRequestRead, Error, void, GetJoinTeamPathParams>, 'path'> &
  GetJoinTeamPathParams;

/**
 * Get campaign id and invited email for a team join key
 *
 * Gets the campaign id and invited email for a team join key.
 */
export const GetJoinTeam = ({ joinKey, ...props }: GetJoinTeamProps) => (
  <Get<JoinTeamRequestRead, Error, void, GetJoinTeamPathParams>
    path={`/api/team/join/${joinKey}`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UseGetJoinTeamProps = Omit<UseGetProps<JoinTeamRequestRead, Error, void, GetJoinTeamPathParams>, 'path'> &
  GetJoinTeamPathParams;

/**
 * Get campaign id and invited email for a team join key
 *
 * Gets the campaign id and invited email for a team join key.
 */
export const useGetJoinTeam = ({ joinKey, ...props }: UseGetJoinTeamProps) =>
  useGet<JoinTeamRequestRead, Error, void, GetJoinTeamPathParams>(
    (paramsInPath: GetJoinTeamPathParams) => `/api/team/join/${paramsInPath.joinKey}`,
    { base: useAtmApiBaseUrl(), requestOptions: useAtmApiRequestOptions(false), pathParams: { joinKey }, ...props },
  );

export interface PostMailResponse {
  success?: boolean;
}

export type PostMailProps = Omit<
  MutateProps<PostMailResponse, {} | Error | ConstraintViolation, void, MailCreateMailRequest, void>,
  'path' | 'verb'
>;

/**
 * Send mails
 *
 * Sends mails to the given attendees via the ATM mail system.
 *
 * The `portalTemplateId` describes the ATM mail template that gets used to create the mail content.
 * You can provide the SBK with the wanted content and they will create the template for you.
 *
 * Dynamic content can be inserted via the `var1`, `var2`, etc. properties.
 */
export const PostMail = (props: PostMailProps) => (
  <Mutate<PostMailResponse, {} | Error | ConstraintViolation, void, MailCreateMailRequest, void>
    verb="POST"
    path={`/api/mails`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UsePostMailProps = Omit<
  UseMutateProps<PostMailResponse, {} | Error | ConstraintViolation, void, MailCreateMailRequest, void>,
  'path' | 'verb'
>;

/**
 * Send mails
 *
 * Sends mails to the given attendees via the ATM mail system.
 *
 * The `portalTemplateId` describes the ATM mail template that gets used to create the mail content.
 * You can provide the SBK with the wanted content and they will create the template for you.
 *
 * Dynamic content can be inserted via the `var1`, `var2`, etc. properties.
 */
export const usePostMail = (props: UsePostMailProps) =>
  useMutate<PostMailResponse, {} | Error | ConstraintViolation, void, MailCreateMailRequest, void>(
    'POST',
    `/api/mails`,
    { base: useAtmApiBaseUrl(), requestOptions: useAtmApiRequestOptions(false), ...props },
  );

export type GetSitesProps = Omit<GetProps<SiteReadCollection[], Error, void, void>, 'path'>;

/**
 * Get all sites
 *
 * Returns a list of all available sites for your portal.
 */
export const GetSites = (props: GetSitesProps) => (
  <Get<SiteReadCollection[], Error, void, void>
    path={`/api/sites`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UseGetSitesProps = Omit<UseGetProps<SiteReadCollection[], Error, void, void>, 'path'>;

/**
 * Get all sites
 *
 * Returns a list of all available sites for your portal.
 */
export const useGetSites = (props: UseGetSitesProps) =>
  useGet<SiteReadCollection[], Error, void, void>(`/api/sites`, {
    base: useAtmApiBaseUrl(),
    requestOptions: useAtmApiRequestOptions(false),
    ...props,
  });

export interface GetSitePathParams {
  /**
   * Site identifier
   */
  name: string;
}

export type GetSiteProps = Omit<GetProps<SiteReadItem, Error, void, GetSitePathParams>, 'path'> & GetSitePathParams;

/**
 * Get a single site
 *
 * Returns the site for the given name.
 */
export const GetSite = ({ name, ...props }: GetSiteProps) => (
  <Get<SiteReadItem, Error, void, GetSitePathParams>
    path={`/api/sites/${name}`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UseGetSiteProps = Omit<UseGetProps<SiteReadItem, Error, void, GetSitePathParams>, 'path'> &
  GetSitePathParams;

/**
 * Get a single site
 *
 * Returns the site for the given name.
 */
export const useGetSite = ({ name, ...props }: UseGetSiteProps) =>
  useGet<SiteReadItem, Error, void, GetSitePathParams>(
    (paramsInPath: GetSitePathParams) => `/api/sites/${paramsInPath.name}`,
    { base: useAtmApiBaseUrl(), requestOptions: useAtmApiRequestOptions(false), pathParams: { name }, ...props },
  );

export interface PostCampaignStatisticResponse {
  success?: boolean;
}

export type PostCampaignStatisticProps = Omit<
  MutateProps<
    PostCampaignStatisticResponse,
    {} | Error | ConstraintViolation,
    void,
    CampaignStatisticUpdateCampaignStatisticRequest,
    void
  >,
  'path' | 'verb'
>;

/**
 * Update campaign statistics
 *
 * Updates the statistics for the given campaign.
 *
 * The previous statistic gets overridden with the new data.
 */
export const PostCampaignStatistic = (props: PostCampaignStatisticProps) => (
  <Mutate<
    PostCampaignStatisticResponse,
    {} | Error | ConstraintViolation,
    void,
    CampaignStatisticUpdateCampaignStatisticRequest,
    void
  >
    verb="POST"
    path={`/api/campaigns/statistics`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UsePostCampaignStatisticProps = Omit<
  UseMutateProps<
    PostCampaignStatisticResponse,
    {} | Error | ConstraintViolation,
    void,
    CampaignStatisticUpdateCampaignStatisticRequest,
    void
  >,
  'path' | 'verb'
>;

/**
 * Update campaign statistics
 *
 * Updates the statistics for the given campaign.
 *
 * The previous statistic gets overridden with the new data.
 */
export const usePostCampaignStatistic = (props: UsePostCampaignStatisticProps) =>
  useMutate<
    PostCampaignStatisticResponse,
    {} | Error | ConstraintViolation,
    void,
    CampaignStatisticUpdateCampaignStatisticRequest,
    void
  >('POST', `/api/campaigns/statistics`, {
    base: useAtmApiBaseUrl(),
    requestOptions: useAtmApiRequestOptions(false),
    ...props,
  });

export interface PostCampaignTypeStatisticResponse {
  success?: boolean;
}

export type PostCampaignTypeStatisticProps = Omit<
  MutateProps<
    PostCampaignTypeStatisticResponse,
    {} | Error | ConstraintViolation,
    void,
    CampaignTypeStatisticWrite,
    void
  >,
  'path' | 'verb'
>;

/**
 * Update the global statistics of your portal
 *
 * Updates the global statistics of your portal.
 *
 * The previous statistic gets overridden with the new data.
 */
export const PostCampaignTypeStatistic = (props: PostCampaignTypeStatisticProps) => (
  <Mutate<PostCampaignTypeStatisticResponse, {} | Error | ConstraintViolation, void, CampaignTypeStatisticWrite, void>
    verb="POST"
    path={`/api/statistics`}
    base={useAtmApiBaseUrl()}
    requestOptions={useAtmApiRequestOptions(false)}
    {...props}
  />
);

export type UsePostCampaignTypeStatisticProps = Omit<
  UseMutateProps<
    PostCampaignTypeStatisticResponse,
    {} | Error | ConstraintViolation,
    void,
    CampaignTypeStatisticWrite,
    void
  >,
  'path' | 'verb'
>;

/**
 * Update the global statistics of your portal
 *
 * Updates the global statistics of your portal.
 *
 * The previous statistic gets overridden with the new data.
 */
export const usePostCampaignTypeStatistic = (props: UsePostCampaignTypeStatisticProps) =>
  useMutate<
    PostCampaignTypeStatisticResponse,
    {} | Error | ConstraintViolation,
    void,
    CampaignTypeStatisticWrite,
    void
  >('POST', `/api/statistics`, { base: useAtmApiBaseUrl(), requestOptions: useAtmApiRequestOptions(false), ...props });
